import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import { PostHogProvider } from 'posthog-js/react';
import { FingerprintProvider } from '@fingerprint/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import CalendarPage from './routes/CalendarPage';
import RequestsPage from './routes/RequestsPage';
import NoAccountPage from './routes/NoAccount';
import NoImpersonationPage from './routes/NoImpersonation';
import TeamPage from './routes/TeamPage';
import OffboardWizardPage from './routes/OffboardWizardPage';
import CustomersPage from './routes/Customers';
import ReportsPage from './routes/ReportsPage';
import ChatPage from './routes/ChatPage';
import SubscriptionPage from './routes/SubscriptionPage';
import ExternalRedirectPage from './routes/ExternalRedirectPage';
import DashboardLayout from './components/global/DashboardLayout';
import { ROUTES } from './constants/global';
import ShopPage from './routes/ShopPage';
import BookingSitePage from './routes/BookingSitePage';
import theme from './constants/theme';
import WaiversPage from './routes/WaiversPage';
import CheckoutPage from './routes/CheckoutsPage';
import PaymentsPage from './routes/PaymentsPage';
import BookingLinksPage from './routes/BookingLinksPage';
import MarketingPage from './routes/MarketingPage';
import ItemsPage from './routes/ItemsPage';
import ImportPage from './routes/ImportPage';
import EmployeeWaiverPage from './routes/EmployeeWaiverPage';
import PlansPage from './routes/PlansPage';
import NotificationsPage from './routes/NotificationsPage';
import ReviewsPage from './routes/ReviewsPage';
import AdsPage from './routes/AdsPage';
import QueuePage from './routes/QueuePage';
import CancellationWaitlistPage from './routes/CancellationWaitlistPage';
import CapitalPage from './routes/CapitalPage';
import ShopFlashbookPage from './routes/ShopFlashbookPage';
import FlashDesignFormPage from './routes/FlashDesignFormPage';
import GetStartedPage from './routes/GetStartedPage';
import LearnPage from './routes/LearnPage';

if (window?.location?.hostname !== 'localhost')
  LicenseInfo.setLicenseKey(
    'f9060b4fd282f513a738e68e4db14624Tz0xMjcwNzMsRT0xODA1NDE0Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI=',
  );

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const posthogOptions = {
  api_host: 'https://us.i.posthog.com',
};

root.render(
  <React.StrictMode>
    <FingerprintProvider
      apiKey={process.env.REACT_APP_FINGERPRINT_KEY!}
      endpoints={['https://metrics.getporter.io']}>
      <PostHogProvider
        apiKey={process.env.REACT_APP_POSTHOG_KEY!}
        options={posthogOptions}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider theme={theme}>
            <BrowserRouter>
              <Routes>
                <Route element={<App />}>
                  {/* Layout with no nav */}
                  <Route
                    element={
                      <DashboardLayout
                        sideNav={false}
                        primaryNav={false}
                        noUser
                      />
                    }>
                    <Route
                      path={`${ROUTES.waiverRequest}/:waiverRequestId/embed`}
                      element={<EmployeeWaiverPage />}
                    />
                  </Route>

                  {/* Authenticated, fullscreen (no nav) */}
                  <Route
                    element={
                      <DashboardLayout sideNav={false} primaryNav={false} />
                    }>
                    <Route
                      path="/team/:employeeId/offboard"
                      element={<OffboardWizardPage />}
                    />
                  </Route>

                  {/* Layout with both side and primary nav */}
                  <Route element={<DashboardLayout />}>
                    <Route path="/" element={<CalendarPage />} />
                    <Route
                      path={ROUTES.getStarted}
                      element={<GetStartedPage />}
                    />
                    <Route path={ROUTES.learn} element={<LearnPage />} />
                    <Route path="/calendar/" element={<CalendarPage />} />
                    <Route
                      path="/calendar/:appointmentId?"
                      element={<CalendarPage />}
                    />
                    <Route path="/checkouts/" element={<CheckoutPage />} />
                    <Route path="/requests/" element={<RequestsPage />} />
                    <Route
                      path="/requests/:appointmentId?"
                      element={<RequestsPage />}
                    />
                    <Route path="/team/" element={<TeamPage />} />
                    <Route
                      path="/customers/:customerId?"
                      element={<CustomersPage />}
                    />
                    <Route path="/reports/" element={<ReportsPage />} />
                    <Route
                      path={ROUTES.subscription}
                      element={<SubscriptionPage />}
                    />
                    <Route path={ROUTES.plans} element={<PlansPage />} />
                    <Route path={ROUTES.chat} element={<ChatPage />} />
                    <Route
                      path={`${ROUTES.chat}/:conversationId`}
                      element={<ChatPage />}
                    />
                    <Route path={ROUTES.shop} element={<ShopPage />} />
                    <Route
                      path={ROUTES.bookingSite}
                      element={<BookingSitePage />}
                    />
                    <Route path={ROUTES.waivers} element={<WaiversPage />} />
                    <Route
                      path={`${ROUTES.waivers}/:waiverId`}
                      element={<WaiversPage />}
                    />
                    <Route path={ROUTES.payments} element={<PaymentsPage />} />
                    <Route path={ROUTES.import} element={<ImportPage />} />
                    <Route
                      path={ROUTES.bookingLinks}
                      element={<BookingLinksPage />}
                    />
                    <Route
                      path={ROUTES.marketing}
                      element={<MarketingPage />}
                    />
                    <Route path={ROUTES.items} element={<ItemsPage />} />
                    <Route
                      path={`${ROUTES.waiverRequest}/:waiverRequestId`}
                      element={<EmployeeWaiverPage />}
                    />
                    <Route
                      path={ROUTES.notifications}
                      element={<NotificationsPage />}
                    />
                    <Route path={ROUTES.reviews} element={<ReviewsPage />} />
                    <Route path={ROUTES.ads} element={<AdsPage />} />
                    <Route path={ROUTES.queue} element={<QueuePage />} />
                    <Route
                      path={ROUTES.cancellationWaitlist}
                      element={<CancellationWaitlistPage />}
                    />
                    <Route path={ROUTES.capital} element={<CapitalPage />} />
                    <Route
                      path={ROUTES.flashbook}
                      element={<ShopFlashbookPage />}
                    />
                    <Route
                      path={`${ROUTES.flashbook}/new`}
                      element={<FlashDesignFormPage />}
                    />
                    <Route
                      path={`${ROUTES.flashbook}/:flashDesignId/edit`}
                      element={<FlashDesignFormPage />}
                    />
                  </Route>

                  <Route
                    element={
                      <DashboardLayout
                        sideNav={false}
                        primaryNav={false}
                        noUser
                      />
                    }>
                    <Route path="/signup" element={<NoAccountPage />} />
                    <Route path="/signup/" element={<NoAccountPage />} />
                    <Route
                      path="/admin/no-impersonation"
                      element={<NoImpersonationPage />}
                    />
                  </Route>

                  <Route
                    path="/external-redirect"
                    element={<ExternalRedirectPage />}
                  />
                  <Route
                    path="/*"
                    element={
                      <main style={{ padding: '1rem' }}>
                        <p>There&apos;s nothing here!</p>
                      </main>
                    }
                  />
                </Route>
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </LocalizationProvider>
      </PostHogProvider>
    </FingerprintProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
